import type { LocalPreference } from "./localPreference.js";

export type ReconcilePreferenceResult<T> = {
	value: T | undefined;
	writeLocal: boolean;
	writeRemote: boolean;
};

/** Newer `updatedAt` wins; ties prefer remote. */
export const reconcilePreference = <T>(
	local: LocalPreference<T> | undefined,
	remote: LocalPreference<T> | undefined,
	isEqual: (a: T, b: T) => boolean = (a, b) => a === b,
): ReconcilePreferenceResult<T> => {
	if (!local && !remote) {
		return { value: undefined, writeLocal: false, writeRemote: false };
	}
	if (!local) {
		return { value: remote?.value, writeLocal: true, writeRemote: false };
	}
	if (!remote) {
		return { value: local.value, writeLocal: false, writeRemote: true };
	}

	const localTime = Date.parse(local.updatedAt);
	const remoteTime = Date.parse(remote.updatedAt);
	// A malformed timestamp can't win a recency comparison — treat it as oldest.
	const localValid = !Number.isNaN(localTime);
	const remoteValid = !Number.isNaN(remoteTime);
	if (!localValid && remoteValid) {
		return { value: remote.value, writeLocal: true, writeRemote: false };
	}
	if (localValid && !remoteValid) {
		return { value: local.value, writeLocal: false, writeRemote: true };
	}

	const winner =
		localValid && remoteValid && localTime > remoteTime ? local : remote;
	const same = isEqual(local.value, remote.value);

	return {
		value: winner.value,
		writeLocal: winner === remote && !same,
		writeRemote: winner === local && !same,
	};
};
